import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/siteData';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = parseInt(id || '0', 10);
  const article = articles.find((a) => a.id === articleId);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="pt-20 container-site min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-4">文章未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles.filter((a) => article.relatedArticles.includes(a.id));

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '新金瓶梅',
    },
    image: article.imageUrl,
    url: `https://xinjinpingmei.com/article/${article.id}`,
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <Navbar />
      <main className="pt-20">
        <div className="container-site max-w-[900px]">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.categoryName, href: `/category/${article.category}` },
              { label: article.title },
            ]}
          />
        </div>

        <article className="container-site max-w-[900px] pb-16">
          <header className="mb-10">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <Link
                to={`/category/${article.category}`}
                className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-primary-100/70 text-primary-700 hover:bg-primary-200 transition-colors duration-200"
              >
                {article.categoryName}
              </Link>
              <span className="text-sm text-foreground-400">{article.date}</span>
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-3 text-sm text-foreground-500">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-user-line"></i>
              </span>
              <span>{article.author}</span>
              <span className="text-foreground-300">|</span>
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-calendar-line"></i>
              </span>
              <span>{article.date}</span>
            </div>
          </header>

          <div className="mb-10 rounded-lg overflow-hidden">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-auto max-h-[450px] object-cover object-top"
              title={`${article.title} - 新金瓶梅`}
            />
          </div>

          <div
            className="prose-article"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-12 pt-8 border-t border-background-200/70">
            <div className="flex items-center gap-2 mb-6">
              <span className="text-sm text-foreground-400">标签：</span>
              <span className="px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700">金瓶梅</span>
              <span className="px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700">{article.categoryName}</span>
              <span className="px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700">古典文学</span>
            </div>

            {relatedArticles.length > 0 && (
              <div>
                <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-5">相关文章推荐</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {relatedArticles.map((rel) => (
                    <ArticleCard key={rel.id} {...rel} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}